export interface Position {
  title: string;
  period: string;
  bullets: string[];
}

export interface ExperienceEntry {
  company: string;
  location: string;
  companyDescription: string;
  positions: Position[];
}

export interface EducationEntry {
  institution: string;
  location: string;
  degree: string;
  concentrations?: string[];
  period: string;
  bullets: string[];
}

export interface SkillGroup {
  category: string;
  items: string[];
}

export interface CertCourse {
  name: string;
  url: string;
}

export interface Certification {
  name: string;
  issuer: string;
  url: string;
  courses: CertCourse[];
}

export interface Project {
  title: string;
  description: string;
  tags: string[];
  notebookUrl?: string;
  link?: { url: string; label: string };
  group: 'no_libraries' | 'with_libraries' | 'misc';
}

export interface ResumeData {
  name: string;
  location: string;
  phone: string;
  email: string;
  linkedin: string;
  summary: string;
  experience: ExperienceEntry[];
  education: EducationEntry[];
  skills: SkillGroup[];
  additionalInfo: string[];
  certifications: Certification[];
  projects: Project[];
}

const resume: ResumeData = {
  name: 'Sreekar Reddy Aparacherla',
  location: 'Chicago, IL',
  phone: '(312) 296-8915',
  email: 'sreekarreddy.aparacherla@ChicagoBooth.edu',
  linkedin: 'https://www.linkedin.com/in/sreekar-reddy-apa/',
  summary:
    'Seasoned full-stack engineer with 7+ years of experience working on enterprise-grade distributed systems. Currently pursuing an MBA with a concentration in Operations at the University of Chicago Booth School of Business, combining deep technical expertise with business acumen to bridge engineering and strategy.',

  experience: [
    {
      company: 'Equinix',
      location: 'Singapore',
      companyDescription:
        'Multinational data center and colocation provider for enterprises',
      positions: [
        {
          title: 'Staff Engineer',
          period: 'November 2022 – August 2025',
          bullets: [
            'Architected and optimized distributed backend services using Spring Boot REST APIs to facilitate seamless data orchestration between back-office systems, enhancing inter-service communication and high-throughput ordering.',
            'Operationalized a Digital Letter of Authorization (DLOA) microservice to automate cross-connect provisioning, increasing transaction throughput by 50% and eliminating manual validation latency.',
            'Leveraged distributed tracing (Zipkin) and log aggregation (Splunk) to identify performance bottlenecks and optimize latency in the customer portal, achieving ~20% faster response times and 99.99% availability.',
          ],
        },
        {
          title: 'Full Stack Engineer III',
          period: 'March 2021 – November 2022',
          bullets: [
            'Engineered customer-facing modules using ReactJS, NodeJS, and a micro frontend (MFE) architecture, optimizing state management and component reusability for a platform serving 50,000+ monthly active users.',
            'Improved system reliability and global latency by migrating and deploying containerized services on AWS cloud infrastructure, utilizing multi-region availability zones to ensure low-latency access.',
            'Automated CI/CD pipelines and Kubernetes/Rancher orchestration for server deployment, reducing manual overhead by 120 hours annually per team while maintaining high-availability (99.99% uptime) targets.',
            'Standardized technical interview rubrics for the 2022 engineering hiring cycle and mentored junior engineers/interns to maintain high code quality and architecture standards.',
          ],
        },
      ],
    },
    {
      company: 'Capgemini',
      location: 'Singapore',
      companyDescription:
        'Consulting and IT services company providing strategy, technology, and digital transformation services',
      positions: [
        {
          title: 'Associate Consultant, Financial Services',
          period: 'August 2019 – October 2020',
          bullets: [
            'Modernized a legacy monolithic cashier system by developing a web-based wraparound application, abstracting complex banking logic behind a modern UI while maintaining system integrity.',
            'Developed middleware services to parse and transform legacy COBOL outputs into JSON/RESTful formats, enabling web-interface compatibility and extending the lifecycle of core banking systems.',
          ],
        },
      ],
    },
    {
      company: 'Mega International',
      location: 'Singapore',
      companyDescription:
        'Multinational SaaS company offering Enterprise Architecture and Governance, Risk & Compliance products',
      positions: [
        {
          title: 'Solutions Engineer',
          period: 'April 2018 – July 2019',
          bullets: [
            'Deployed and configured HOPEX SaaS platform across enterprise client-server environments, managing load balancing and server-side configurations to support 100+ concurrent users.',
            'Automated asynchronous nightly cron jobs using VBScript to generate real-time data visualizations and notification triggers for risk-management modules, reducing manual reporting toil by 15 hours monthly.',
          ],
        },
      ],
    },
  ],

  education: [
    {
      institution: 'The University of Chicago Booth School of Business',
      location: 'Chicago, IL',
      degree: 'Master of Business Administration',
      concentrations: ['Operations'],
      period: 'September 2025 – June 2027',
      bullets: [
        'Member of Booth Technology Group',
        'GRE: 334/340',
      ],
    },
    {
      institution: 'National University of Singapore',
      location: 'Singapore',
      degree: 'Bachelor of Engineering (Chemical) With Honors',
      period: 'August 2013 – May 2017',
      bullets: [
        'NUS Science and Technology Scholarship',
        'Varsity Athlete and Team Manager (Cricket)',
      ],
    },
  ],

  skills: [
    {
      category: 'Languages',
      items: ['Java', 'JavaScript', 'TypeScript', 'Python', 'VBScript', 'COBOL (integration)'],
    },
    {
      category: 'Frontend',
      items: ['ReactJS', 'Micro Frontend (MFE)', 'NodeJS', 'HTML/CSS'],
    },
    {
      category: 'Backend & APIs',
      items: ['Spring Boot', 'REST APIs', 'Microservices', 'Middleware Development'],
    },
    {
      category: 'Cloud & DevOps',
      items: ['AWS', 'Docker', 'Kubernetes', 'Rancher', 'CI/CD Pipelines'],
    },
    {
      category: 'Observability',
      items: ['Zipkin (Distributed Tracing)', 'Splunk (Log Aggregation)'],
    },
    {
      category: 'Architecture',
      items: ['Distributed Systems', 'Monolith-to-Microservices', 'High Availability Design'],
    },
  ],

  additionalInfo: [
    'Singapore Prisons Volunteer',
    'PADI certified open water diver; SSI certified advanced open water diver',
    'Finisher of the Singapore Marathon 2019',
    'Work authorized in the US via H1B1 (Singaporean citizens); not susceptible to H1B lottery',
  ],

  certifications: [
    {
      name: 'Machine Learning Specialization',
      issuer: 'DeepLearning.AI',
      url: 'https://learn.deeplearning.ai/certificates/9435b4ce-6c01-40f5-9e52-389f792fe509',
      courses: [
        {
          name: 'Supervised Machine Learning: Regression and Classification',
          url: 'https://learn.deeplearning.ai/certificates/5362b35d-da99-4b7d-a3fb-65124e135b89',
        },
        {
          name: 'Advanced Learning Algorithms',
          url: 'https://learn.deeplearning.ai/certificates/a2a7cc19-a01a-4df2-9131-eb280b3ce06b',
        },
        {
          name: 'Unsupervised Learning, Recommenders, Reinforcement Learning',
          url: 'https://learn.deeplearning.ai/certificates/043ffa4a-fe29-4711-bc77-92631a42ce4e',
        },
      ],
    },
    {
      name: 'Mathematics for Machine Learning and Data Science',
      issuer: 'DeepLearning.AI',
      url: 'https://learn.deeplearning.ai/certificates/662b0c00-c5b2-419a-b0e0-57d4be96e0fe',
      courses: [
        {
          name: 'Linear Algebra for Machine Learning and Data Science',
          url: 'https://learn.deeplearning.ai/certificates/2be41e66-962e-4da4-931c-ba434416c8a8',
        },
        {
          name: 'Calculus for Machine Learning and Data Science',
          url: 'https://learn.deeplearning.ai/certificates/bcf39b95-6aca-4462-852a-de6876b48d0e',
        },
        {
          name: 'Probability & Statistics for Machine Learning & Data Science',
          url: 'https://learn.deeplearning.ai/certificates/46c464d7-e398-4637-a956-bfa483dc97f7',
        },
      ],
    },
    {
      name: 'CFA Level 1',
      issuer: 'CFA Institute',
      url: 'https://credentials.cfainstitute.org/cc38e2e8-cd21-4786-8e44-1cd28dd211b5',
      courses: [],
    },
  ],

  projects: [
    {
      title: 'Neural Network',
      description:
        'Built a multi-layer neural network using only NumPy. No ML frameworks. Implemented forward propagation, ReLU activations, softmax output, and backpropagation by hand. Trained on MNIST to classify handwritten digits.',
      tags: ['NumPy', 'Neural Networks', 'Backpropagation', 'MNIST'],
      notebookUrl: '/notebooks/numpy_pd_neural_network.html',
      group: 'no_libraries',
    },
    {
      title: 'Decision Tree & Random Forest',
      description:
        'Implemented a decision tree classifier from first principles using NumPy and Pandas, then extended it into a random forest with feature subsampling and bootstrap aggregation. Applied to the UCI Bank Marketing dataset to predict term deposit subscriptions.',
      tags: ['NumPy', 'Pandas', 'Decision Trees', 'Random Forest', 'Classification'],
      notebookUrl: '/notebooks/numpy_pd_decision_trees.html',
      group: 'no_libraries',
    },
    {
      title: 'Logistic Regression',
      description:
        'Derived and implemented logistic regression with gradient descent using NumPy. Applied to Titanic survival prediction with extensive feature engineering: extracting titles from names, handling missing values, and encoding categoricals.',
      tags: ['NumPy', 'Pandas', 'Logistic Regression', 'Feature Engineering', 'Titanic'],
      notebookUrl: '/notebooks/numpy_pd_logistic_regression.html',
      group: 'no_libraries',
    },
    {
      title: 'Principal Component Analysis',
      description:
        'Implemented PCA from scratch using NumPy: standardization, covariance matrix, eigendecomposition, and projection onto principal components. Applied to the Iris dataset and visualized class separation in 2D principal component space.',
      tags: ['NumPy', 'Pandas', 'PCA', 'Dimensionality Reduction', 'Unsupervised Learning'],
      notebookUrl: '/notebooks/principal_component_analysis.html',
      group: 'no_libraries',
    },
    {
      title: 'K-Means Clustering',
      description:
        'Implemented K-Means clustering from scratch using NumPy. Applied to the Iris dataset to group plants by species without labels, using Euclidean distance and centroid updates. Evaluated cluster quality against ground-truth labels.',
      tags: ['NumPy', 'Pandas', 'K-Means', 'Clustering', 'Unsupervised Learning'],
      notebookUrl: '/notebooks/k_means_clustering.html',
      group: 'no_libraries',
    },
    {
      title: 'K-Nearest Neighbours',
      description:
        'Implemented KNN from scratch using NumPy with Euclidean distance. Applied to the MovieLens dataset as a content-based movie recommender: given a film, finds the k most similar titles by genre and release date.',
      tags: ['NumPy', 'Pandas', 'KNN', 'Recommender Systems', 'MovieLens'],
      notebookUrl: '/notebooks/k_nearest_neighbours.html',
      group: 'no_libraries',
    },
    {
      title: 'Linear Regression',
      description:
        'Implemented multivariate linear regression using the closed-form normal equation with NumPy. Applied to the California Housing Prices dataset with feature normalization and matrix algebra. No sklearn involved.',
      tags: ['NumPy', 'Pandas', 'Linear Regression', 'California Housing'],
      notebookUrl: '/notebooks/numpy_pd_linear_regression.html',
      group: 'no_libraries',
    },
    {
      title: 'Telco Customer Churn',
      description:
        'End-to-end churn prediction on a Kaggle telco dataset. Covers feature engineering on mixed-type columns (binary, ordinal, multi-class categoricals), model training, and evaluation.',
      tags: ['scikit-learn', 'Pandas', 'Classification', 'Feature Engineering', 'Churn'],
      notebookUrl: '/notebooks/telco_churn.html',
      group: 'with_libraries',
    },
    {
      title: 'Memo Builder',
      description:
        'Generates Investment Committee memos for an early-stage VC fund. Reads a pitch deck (PDF pages as text and images so charts are captured, or PPTX slides), dated partner call notes, and the fund thesis, then streams a cited memo from Claude through a Firebase Cloud Function.',
      tags: ['React', 'TypeScript', 'Claude API', 'Cloud Functions', 'pdf.js', 'Venture Capital'],
      link: { url: '/memo-builder', label: 'Open Memo Builder' },
      group: 'misc',
    },
  ],
};

export default resume;
